// AdminLetstalk.jsx
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Styles from "./Letstalk.module.css";
import SearchInput from "../../components/searchinput/SearchInput";
import DataTable from "../../components/datatable/DataTable";
import Pagination from "../../components/pagination/Pagination";
import moment from "moment";
import dayjs from "dayjs";
import isSameOrOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AxiosInstance } from "../../axios/index";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrOrBefore);
dayjs.extend(customParseFormat);

const getInitialDateLabelFromParams = (from, to) => {
  const today = dayjs().format("DD/MM/YYYY");
  const last7DaysFrom = dayjs().subtract(6, "days").format("DD/MM/YYYY");
  const last30DaysFrom = dayjs().subtract(29, "days").format("DD/MM/YYYY");
  const thisMonthStart = dayjs().startOf("month").format("DD/MM/YYYY");
  const thisMonthEnd = dayjs().endOf("month").format("DD/MM/YYYY");

  if (from === today && to === today) return "Today";
  if (from === last7DaysFrom && to === today) return "Last 7 Days";
  if (from === last30DaysFrom && to === today) return "Last 30 Days";
  if (from === thisMonthStart && to === thisMonthEnd) return "This Month";
  if (from && to) return `${from} - ${to}`;
  if (from) return `From ${from}`;
  if (to) return `To ${to}`;
  return "";
};

const AdminLetstalk = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterRef = useRef(null);

  const initialFilters = {
    search: searchParams.get("search") || "",
    userType: searchParams.get("userType") || "",
    fromDate: searchParams.get("fromDate") || "",
    toDate: searchParams.get("toDate") || "",
    appliedDateLabel: getInitialDateLabelFromParams(
      searchParams.get("fromDate") || "",
      searchParams.get("toDate") || ""
    ),
    tempCategoricalFilters: { user_type: "" },
    tempFromDate: "",
    tempToDate: "",
    tempDateLabel: "",
  };

  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState({
    search: initialFilters.search,
    userType: initialFilters.userType,
    fromDate: initialFilters.fromDate,
    toDate: initialFilters.toDate,
  });
  
  const location = useLocation();


  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chipData, setChipData] = useState([]);
  const [isOpen, setIsOpen] = useState({ status: false, column: "" });
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(searchParams.get("limit")) || 10
  );
  const rowsPerPageOptions = [10, 15, 20, 50];

  // Columns
  const columns = useMemo(
    () => [
      { header: "S.No", accessor: "sno" },
      { header: "Name", accessor: "full_name", sortable: true },
      { header: "Email Address", accessor: "email", sortable: true },
      { header: "Phone", accessor: "phone_number", sortable: true },
      { header: "Company Name", accessor: "company_name", sortable: true },
      { header: "Website URL", accessor: "website_url", sortable: true },
      {
        header: "User Type",
        accessor: "user_type",
        sortable: true,
        filterable: true,
      },
      {
        header: "Reached Out On",
        accessor: "reached_out_on",
        sortable: true,
        filterable: true,
        isDateColumn: true,
      },
    ],
    []
  );

  const filterOptions = useMemo(
    () => ({
      user_type: [
        { label: "Individual", value: "Individual" },
        { label: "Company", value: "Company" },
      ],
    }),
    []
  );

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.userType) params.set("userType", filters.userType);
    if (filters.fromDate) params.set("fromDate", filters.fromDate);
    if (filters.toDate) params.set("toDate", filters.toDate);
    if (currentPage !== 1) params.set("page", currentPage.toString());
    if (rowsPerPage !== 10) params.set("limit", rowsPerPage.toString());
    setSearchParams(params);
  }, [filters, currentPage, rowsPerPage, setSearchParams]);

  // Update chips
  useEffect(() => {
    const currentChips = [];
    if (filters.search)
      currentChips.push({
        type: "search",
        value: filters.search,
        label: `Search: ${filters.search}`,
      });
    if (filters.userType)
      currentChips.push({
        label: `User Type: ${filters.userType}`,
        type: "user_type",
        value: filters.userType,
      });
    if (filters.fromDate || filters.toDate)
      currentChips.push({
        label: `Reached Out On: ${filters.appliedDateLabel}`,
        type: "reached_out_on",
        value: `${filters.fromDate}-${filters.toDate}`,
      });
    setChipData(currentChips);
  }, [filters]);

useEffect(() => {
  if (location.state?.appliedFilters) {
    const filtersFromView = location.state.appliedFilters;
    setFilters((prev) => ({
      ...prev,
      search: filtersFromView.search || "",
      userType: filtersFromView.userType || "",
      fromDate: filtersFromView.fromDate || "",
      toDate: filtersFromView.toDate || "",
      appliedDateLabel: getInitialDateLabelFromParams(
        filtersFromView.fromDate || "",
        filtersFromView.toDate || ""
      ),
    }));

    setAppliedFilters({
      search: filtersFromView.search || "",
      userType: filtersFromView.userType || "",
      fromDate: filtersFromView.fromDate || "",
      toDate: filtersFromView.toDate || "",
    });
  }
}, [location.state]);
  // Fetch users ONLY when appliedFilters change
  const fetchAllUsers = useCallback(async () => {
    setLoading(true);
    try {
      const payloadUserType =
        appliedFilters.userType === "Individual"
          ? 1
          : appliedFilters.userType === "Company"
          ? 0
          : null;
      const payloadFromDate = appliedFilters.fromDate
        ? moment(appliedFilters.fromDate, "DD/MM/YYYY").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : "";
      const payloadToDate = appliedFilters.toDate
        ? moment(appliedFilters.toDate, "DD/MM/YYYY").format(
            "YYYY-MM-DD HH:mm:ss"
          )
        : "";

      const res = await AxiosInstance.post("/api/letstalk/list", {
        user_type: payloadUserType,
        search: appliedFilters.search,
        fromDate: payloadFromDate,
        toDate: payloadToDate,
      });

      const clientList = Array.isArray(res?.data?.clients?.[0])
        ? res.data.clients[0]
        : res.data.clients || [];
      setAllUsers(clientList);
    } catch (error) {
      console.error("Error fetching all users:", error);
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  // Filter users client-side
  const filteredUsers = useMemo(() => {
    let data = allUsers;
    if (filters.search) {
      const term = filters.search.toLowerCase();
      data = data.filter(
        (u) =>
          u.full_name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term) ||
          u.phone_number?.includes(term)
      );
    }
    if (filters.userType) {
      data = data.filter(
        (u) =>
          (u.user_type === 1 && filters.userType === "Individual") ||
          (u.user_type === 0 && filters.userType === "Company")
      );
    }
    if (filters.fromDate && filters.toDate) {
      const start = dayjs(filters.fromDate, "DD/MM/YYYY").startOf("day");
      const end = dayjs(filters.toDate, "DD/MM/YYYY").endOf("day");
      data = data.filter((u) => {
        if (!u.reached_out_on) return false;
        const d = dayjs(u.reached_out_on);
        return d.isSameOrAfter(start) && d.isSameOrBefore(end);
      });
    }
    return data;
  }, [allUsers, filters]);

  // Pagination
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, currentPage, rowsPerPage]);

  const processedUsers = useMemo(
    () =>
      paginatedUsers.map((user, i) => ({
        sno: (currentPage - 1) * rowsPerPage + i + 1,
        id: user.id || "-",
        full_name: user.full_name || "-",
        email: user.email || "-",
        phone_number: user.phone_number || "-",
        company_name: user.company_name || "-",
        website_url: user.website_url || "-",
        user_type: user.user_type === 1 ? "Individual" : "Company",
        reached_out_on: user.reached_out_on
          ? moment(user.reached_out_on).format("DD/MM/YYYY")
          : "-",
      })),
    [paginatedUsers, currentPage, rowsPerPage]
  );

  // Handlers
const handleSearch = useCallback((val) => {
  setFilters((prev) => ({ ...prev, search: val }));
  setAppliedFilters((prev) => ({ ...prev, search: val })); // <-- add this line
  setCurrentPage(1);
}, []);


  const handleDeleteChip = useCallback((chip) => {
    if (chip.type === "search") {
      setFilters((prev) => ({ ...prev, search: "" }));
      setAppliedFilters((prev) => ({ ...prev, search: "" }));
    }

    if (chip.type === "user_type") {
      setFilters((prev) => ({
        ...prev,
        userType: "",
        tempCategoricalFilters: { user_type: "" },
      }));
      setAppliedFilters((prev) => ({ ...prev, userType: "" }));
    }

    if (chip.type === "reached_out_on") {
      setFilters((prev) => ({
        ...prev,
        fromDate: "",
        toDate: "",
        appliedDateLabel: "",
        tempFromDate: "",
        tempToDate: "",
        tempDateLabel: "",
      }));
      setAppliedFilters((prev) => ({ ...prev, fromDate: "", toDate: "" }));
    }

    setCurrentPage(1);
  }, []);

  const handleAllClear = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      search: "",
      userType: "",
      fromDate: "",
      toDate: "",
      appliedDateLabel: "",
      tempFromDate: "",
      tempToDate: "",
      tempDateLabel: "",
      tempCategoricalFilters: { user_type: "" },
    }));
    setAppliedFilters({
      search: "",
      userType: "",
      fromDate: "",
      toDate: "",
    });
    setCurrentPage(1);
  }, []);

  const handleFilterClick = useCallback((col) => {
    setIsOpen({ status: true, column: col });
    if (col === "user_type") {
      setFilters((prev) => ({
        ...prev,
        tempCategoricalFilters: { user_type: prev.userType },
      }));
    } else if (col === "reached_out_on") {
      setFilters((prev) => ({
        ...prev,
        tempFromDate: prev.fromDate,
        tempToDate: prev.toDate,
        tempDateLabel: prev.appliedDateLabel,
      }));
    }
  }, []);

  const handleApplyFilter = useCallback(() => {
  setFilters((prev) => ({
    ...prev,
    userType: prev.tempCategoricalFilters.user_type,
  }));

  setAppliedFilters((prev) => ({
    ...prev,
    userType: filters.tempCategoricalFilters.user_type,
    // Keep other filters as they are
    search: filters.search,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
  }));

  setIsOpen({ status: false, column: "" });
  setCurrentPage(1);
}, [filters]);
const handleClearSpecificFilter = useCallback((col) => {
  if (col === "user_type") {
    setFilters((prev) => ({
      ...prev,
      userType: "",
      tempCategoricalFilters: { user_type: "" },
    }));
    
    // Also update appliedFilters to clear the API state
    setAppliedFilters((prev) => ({
      ...prev,
      userType: "",
    }));
  } else if (col === "reached_out_on") {
    setFilters((prev) => ({
      ...prev,
      fromDate: "",
      toDate: "",
      appliedDateLabel: "",
      tempFromDate: "",
      tempToDate: "",
      tempDateLabel: "",
    }));
    
    // Also update appliedFilters to clear the API state
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: "",
      toDate: "",
    }));
  }
  
  setIsOpen({ status: false, column: "" });
  setCurrentPage(1);
}, []);
  const handleDateRangeApply = useCallback(
    ({ fromDate: from, toDate: to, label, selectedLabel = "" }) => {
      setFilters((prev) => ({
        ...prev,
        fromDate: from,
        toDate: to,
        appliedDateLabel: selectedLabel || label,
        tempFromDate: from,
        tempToDate: to,
        tempDateLabel: selectedLabel || label,
      }));

      setAppliedFilters((prev) => ({
        search: filters.search,
        userType: filters.userType,
        fromDate: from,
        toDate: to,
      }));

      setIsOpen({ status: false, column: "" });
      setCurrentPage(1);
    },
    [filters]
  );

  const handleCategoricalFilterChange = useCallback((column, value) => {
    setFilters((prev) => ({
      ...prev,
      tempCategoricalFilters: {
        ...prev.tempCategoricalFilters,
        [column]: value,
      },
    }));
  }, []);

  const handleClickOutside = useCallback((e) => {
    if (
      filterRef.current &&
      !filterRef.current.contains(e.target) &&
      !e.target.closest('img[alt="filter"]')
    ) {
      setIsOpen((prev) => {
        if (prev.status) {
          if (prev.column === "user_type") {
            setFilters((f) => ({
              ...f,
              tempCategoricalFilters: { user_type: f.userType },
            }));
          } else if (prev.column === "reached_out_on") {
            setFilters((f) => ({
              ...f,
              tempFromDate: f.fromDate,
              tempToDate: f.toDate,
              tempDateLabel: f.appliedDateLabel,
            }));
          }
        }
        return { ...prev, status: false };
      });
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const handleViewClick = useCallback(
    (row) => {
      navigate("/admin/lets-talk/view", {
        state: {
          email: row.email,
          id: row.id,
          appliedFilters: appliedFilters, // include it inside state
        },
      });
    },
    [navigate, appliedFilters] // add appliedFilters to dependencies
  );

  const actionIconProps = useMemo(
    () => ({
      defaultIcon: "",
      hoverIcon: "",
      onClick: handleViewClick,
    }),
    [handleViewClick]
  );

  return (
    <div className={`container-fluid px-3 ${Styles.main_Container}`}>
      <div className="row mb-3">
        <div className="col-12 col-md-6">
          <SearchInput
            value={filters.search} // ✅ pass value from parent
            handleKeyDown={handleSearch}
            placeholder="Search with Name, Email, Phone..."
          />
        </div>
      </div>

      <div className="mb-3">
        {chipData.map((chip, i) => (
          <span
            key={i}
            className="custom-badge me-2 rounded-md py-2 px-3 flex items-center space-x-2"
          >
            {chip.label}
            <button
              onClick={() => handleDeleteChip(chip)}
              className="btn-close  ms-2"
            ></button>
          </span>
        ))}
        {chipData.length > 0 && (
          <button
            className="btn btn-sm btn-outline-danger ms-2 rounded-md py-2 px-3"
            onClick={handleAllClear}
          >
            Clear All Filters
          </button>
        )}
      </div>

      <DataTable
        loading={loading}
        columns={columns}
        data={processedUsers}
        filterOptions={filterOptions}
        filters={{
          user_type: filters.userType,
          reached_out_on_from: filters.fromDate,
          reached_out_on_to: filters.toDate,
        }}
        actionIcon={actionIconProps}
        handleFilter={handleFilterClick}
        isOpens={isOpen}
        filterPopupRef={filterRef}
        currentDateRange={
          isOpen.status && isOpen.column === "reached_out_on"
            ? {
                fromDate: filters.tempFromDate,
                toDate: filters.tempToDate,
                label: filters.tempDateLabel,
              }
            : null
        }
        onDateRangeApply={handleDateRangeApply}
        tempCategoricalFilters={filters.tempCategoricalFilters}
        onCategoricalFilterChange={handleCategoricalFilterChange}
        handleApplyFilter={handleApplyFilter}
        onClearSpecificFilter={handleClearSpecificFilter}
        ellipsisIndex={[2, 4, 5]}
      />

      <div className="mb-3 d-flex justify-content-end">
        <Pagination
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalItems={filteredUsers.length}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </div>
    </div>
  );
};

export default AdminLetstalk;